import { FC, useState, ChangeEvent, useMemo, useCallback, useEffect, MouseEvent, useRef } from 'react';
import { Button, DashboardCard, Loading, Modal, Table, Tag } from '../../../../components';
import {
	AdminLojistaLista,
	AdminLojistaTotais,
	deleteBuyer,
	getAdminLojistasList,
	getAdminLojistasTotals,
} from '../../../../services';
import { RegistrationPage } from '../../templates';
import { RegistrationEntities, TagActiveLabel, useDebounce } from '../../../../utils';
import { ActionsContainer, Dashboard, TableWrapper, TagsContainer } from '../../Registration.styles';
import { Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { DeleteOutlined, EditOutlined, GroupsOutlined } from '@mui/icons-material';
import { useToastStore } from '../../../../stores';
import { DashboardStatus } from '../../../../components/dashboard-card/DashboardCard';
import { TagDeferralLabel } from '../../../../utils/enums/TagDeferralLabel';

const emptyTotals: AdminLojistaTotais = {
	qtd_ativo: 0,
	qtd_inativo: 0,
	qtd_pendente: 0,
};

const statusFromCard = (card: DashboardStatus | null) => {
	if (card === DashboardStatus.Active) return 'Ativo';
	if (card === DashboardStatus.Inactive) return 'Inativo';
	if (card === DashboardStatus.Pending) return 'Pendente';
	return undefined;
};

const RegistrationShopkeepers: FC = () => {
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState<boolean>(true);
	const [tableLoading, setTableLoading] = useState<boolean>(false);
	const [shopkeepers, setShopkeepers] = useState<AdminLojistaLista[]>([]);
	const [totals, setTotals] = useState<AdminLojistaTotais>(emptyTotals);
	const [searchTerm, setSearchTerm] = useState('');
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [shopkeeperToDelete, setShopkeeperToDelete] = useState<number>();
	const [activeCard, setActiveCard] = useState<DashboardStatus | null>(null);
	const debouncedSearch = useDebounce(searchTerm, 500);
	const initialLoad = useRef(true);

	const tableColumns = useMemo(
		() => [
			{ id: 'dataCadastro', label: 'Cadastro' },
			{ id: 'razaoSocial', label: 'Lojista' },
			{ id: 'document', label: 'CNPJ/CPF' },
			{ id: 'status', label: 'Status' },
			{ id: 'actions', label: 'Ações' },
		],
		[]
	);

	const defineActiveLabel = useCallback((status: string) => {
		if (status === TagActiveLabel.Active) return TagActiveLabel.Active;
		if (status === TagActiveLabel.Inactive) return TagActiveLabel.Inactive;
		if (status === TagDeferralLabel.Pending) return TagDeferralLabel.Pending;
		return null;
	}, []);

	const handleDeleteConfirm = useCallback((id: number) => {
		setOpenDeleteModal(true);
		setShopkeeperToDelete(id);
	}, []);

	const rowMapper = useCallback(
		(item: AdminLojistaLista) => {
			const label = defineActiveLabel(item.status);
			const cadastro = item.dat_cadastro ? new Date(item.dat_cadastro) : null;

			return {
				dataCadastro: {
					value: cadastro ? cadastro.toLocaleDateString('pt-BR') : '',
					rawValue: cadastro ? cadastro.getTime() : 0,
				},
				razaoSocial: {
					value: item.nome,
					rawValue: item.nome,
				},
				document: { value: item.documento, rawValue: item.documento },
				status: {
					value: label ? (
						<TagsContainer>
							<Tag status={label} />
						</TagsContainer>
					) : (
						item.status
					),
					rawValue: item.status,
				},
				actions: {
					value: (
						<ActionsContainer>
							<Tooltip title='Editar' placement='top' arrow>
								<Link to={`editar/${item.id}`}>
									<Button variant='icon' icon={<EditOutlined fontSize='inherit' />} />
								</Link>
							</Tooltip>
							<Tooltip title='Desativar' placement='top' arrow>
								<div>
									<Button
										variant='icon'
										icon={<DeleteOutlined fontSize='inherit' />}
										onClick={() => handleDeleteConfirm(item.id)}
									/>
								</div>
							</Tooltip>
							<Tooltip title='Usuários' placement='top' arrow>
								<Link to={`/cadastros/usuarios/revenda-${item.id}`}>
									<Button variant='icon' icon={<GroupsOutlined fontSize='inherit' />} />
								</Link>
							</Tooltip>
						</ActionsContainer>
					),
				},
			};
		},
		[handleDeleteConfirm, defineActiveLabel]
	);

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
	};

	const handleDelete = async () => {
		try {
			const response = await deleteBuyer({ id: shopkeeperToDelete!.toString() });

			if (!response.includes('sucesso')) {
				setToast({ message: response, variant: 'error' });
			} else {
				setToast({
					message: 'Revenda removida com sucesso!',
					variant: 'success',
				});
				await Promise.all([loadShopkeepers(), loadTotals()]);
			}
		} catch {
			setToast({
				message: 'Erro ao remover a revenda!',
				variant: 'error',
			});
		} finally {
			setOpenDeleteModal(false);
		}
	};

	const handleFilterChange = (status: DashboardStatus) => {
		if (status === activeCard) {
			setActiveCard(null);
			return;
		}

		setActiveCard(status);
	};

	const handleClearSearch = (_e: MouseEvent<HTMLButtonElement>) => {
		setSearchTerm('');
	};

	const loadShopkeepers = useCallback(async () => {
		const list = await getAdminLojistasList(debouncedSearch, statusFromCard(activeCard));
		setShopkeepers(list);
	}, [debouncedSearch, activeCard]);

	const loadTotals = useCallback(async () => {
		const totais = await getAdminLojistasTotals();
		setTotals(totais ?? emptyTotals);
	}, []);

	const MemoizedTable = useMemo(
		() => <Table columns={tableColumns} rows={shopkeepers?.map(rowMapper)} paginate />,
		[shopkeepers, rowMapper, tableColumns]
	);

	useEffect(() => {
		let cancelled = false;

		const fetchData = async () => {
			const first = initialLoad.current;
			if (first) setLoading(true);
			else setTableLoading(true);

			try {
				const listPromise = getAdminLojistasList(debouncedSearch, statusFromCard(activeCard));
				const totalsPromise = first ? getAdminLojistasTotals() : Promise.resolve(null);
				const [list, totais] = await Promise.all([listPromise, totalsPromise]);

				if (cancelled) return;

				setShopkeepers(list ?? []);
				if (totais) setTotals(totais);
			} catch {
				if (!cancelled) {
					setToast({
						message: 'Erro ao buscar as revendas!',
						variant: 'error',
					});
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
					setTableLoading(false);
					initialLoad.current = false;
				}
			}
		};

		fetchData();

		return () => {
			cancelled = true;
		};
	}, [debouncedSearch, activeCard, setToast]);

	if (loading) return <Loading fullScreen />;

	return (
		<>
			<Dashboard>
				<DashboardCard
					active={activeCard === DashboardStatus.Active}
					variant={DashboardStatus.Active}
					amount={totals.qtd_ativo}
					onClick={() => handleFilterChange(DashboardStatus.Active)}
				/>
				<DashboardCard
					active={activeCard === DashboardStatus.Inactive}
					variant={DashboardStatus.Inactive}
					amount={totals.qtd_inativo}
					onClick={() => handleFilterChange(DashboardStatus.Inactive)}
				/>
				<DashboardCard
					active={activeCard === DashboardStatus.Pending}
					variant={DashboardStatus.Pending}
					amount={totals.qtd_pendente}
					onClick={() => handleFilterChange(DashboardStatus.Pending)}
				/>
			</Dashboard>
			<RegistrationPage
				entity={RegistrationEntities.shopkeeper}
				searchTerm={searchTerm}
				onChange={handleSearchChange}
				onClear={handleClearSearch}
			>
				<>
					<Modal
						open={openDeleteModal}
						onClose={() => setOpenDeleteModal(false)}
						title='Confirmação!'
						primaryButtonText='Sim'
						secondaryButtonText='Não'
						onPrimaryButtonClick={handleDelete}
						onSecondaryButtonClick={() => setOpenDeleteModal(false)}
					>
						Deseja realmente desativar?
					</Modal>
					<TableWrapper>{tableLoading ? <Loading /> : MemoizedTable}</TableWrapper>
				</>
			</RegistrationPage>
		</>
	);
};

export default RegistrationShopkeepers;
